import type { ReportSectionDto } from '@act/shared/data-transfer-objects';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FlexContainer, IconLibrary } from '@act/storybook';
import { Box, Typography } from '@mui/material';
import { CSS } from '@dnd-kit/utilities';
import { getSectionMeta } from '../utils/reportDisplay.util';
import ReportsSurface from './ReportsSurface';
import ReportSectionSettingToggle from './ReportSectionSettingToggle';

/** Fixed sidebar width so the editor pane keeps most of the horizontal space. */
const SECTIONS_SIDEBAR_WIDTH = '300px';

export interface ReportBuilderSectionListProps {
  sections: ReportSectionDto[];
  selectedSectionId: string | null;
  savingSection: boolean;
  reordering: boolean;
  onSelect: (sectionRowId: string) => void;
  onIncludedChange: (section: ReportSectionDto, included: boolean) => void;
  onCalloutEnabledChange: (
    section: ReportSectionDto,
    calloutEnabled: boolean,
  ) => void;
  onReorder: (activeId: string, overId: string) => void;
}

interface SortableSectionRowProps {
  section: ReportSectionDto;
  index: number;
  isSelected: boolean;
  isLast: boolean;
  savingSection: boolean;
  onSelect: (sectionRowId: string) => void;
  onIncludedChange: (section: ReportSectionDto, included: boolean) => void;
  onCalloutEnabledChange: (
    section: ReportSectionDto,
    calloutEnabled: boolean,
  ) => void;
}

/**
 * Single draggable row in the builder section sidebar.
 */
const SortableSectionRow = ({
  section,
  index,
  isSelected,
  isLast,
  savingSection,
  onSelect,
  onIncludedChange,
  onCalloutEnabledChange,
}: SortableSectionRowProps) => {
  const meta = getSectionMeta(section.sectionId);
  const title = meta?.title ?? section.sectionId;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Box
        style={{
          padding: '5px 8px 5px 4px',
          backgroundColor: isSelected ? '#f3f1ec' : 'transparent',
          borderBottom: isLast ? undefined : '1px solid rgba(0, 0, 0, 0.06)',
          borderLeft: isSelected
            ? '2px solid rgba(0, 0, 0, 0.55)'
            : '2px solid transparent',
        }}
      >
        <FlexContainer
          direction="row"
          alignItems="center"
          gap={0.5}
          width="100%"
          height="auto"
          overflowAllVisible={true}
        >
          <Box
            ref={setActivatorNodeRef}
            aria-label={`Reorder ${title}`}
            {...attributes}
            {...listeners}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'grab',
              touchAction: 'none',
              color: 'rgba(0, 0, 0, 0.4)',
              backgroundColor: 'transparent',
              border: 'none',
              padding: 0,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <IconLibrary iconName="DotsSixVertical" size={16} weight="bold" />
          </Box>
          <FlexContainer
            direction="column"
            gap={0.25}
            flexGrow={1}
            minWidth="0"
            height="auto"
            overflowAllVisible={true}
          >
            <Typography
              variant="body2"
              noWrap
              title={`#${index + 1} ${title}`}
              onClick={() => onSelect(section.id)}
            >
              #{index + 1} {title}
            </Typography>
            <FlexContainer
              direction="row"
              gap={1}
              justifyContent="flex-end"
              alignItems="center"
              width="100%"
              height="auto"
              overflowAllVisible={true}
            >
              <ReportSectionSettingToggle
                compact
                label="On"
                value={section.included}
                disabled={savingSection}
                onChange={included => onIncludedChange(section, included)}
              />
              {meta?.supportsCallout ? (
                <ReportSectionSettingToggle
                  compact
                  label="Callout"
                  value={section.calloutEnabled}
                  disabled={savingSection || !section.included}
                  onChange={calloutEnabled =>
                    onCalloutEnabledChange(section, calloutEnabled)
                  }
                />
              ) : null}
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
      </Box>
    </Box>
  );
};

/**
 * Drag-and-drop sidebar listing all report sections with quick toggles.
 */
const ReportBuilderSectionList = ({
  sections,
  selectedSectionId,
  savingSection,
  reordering,
  onSelect,
  onIncludedChange,
  onCalloutEnabledChange,
  onReorder,
}: ReportBuilderSectionListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || reordering) {
      return;
    }
    onReorder(String(active.id), String(over.id));
  };

  return (
    <FlexContainer
      direction="column"
      width={SECTIONS_SIDEBAR_WIDTH}
      height="100%"
      minHeight="0"
      flexShrink={0}
      overflow="hidden"
    >
      <ReportsSurface padding={0} width="100%" height="100%" flexShrink={0}>
        <FlexContainer
          direction="column"
          width="100%"
          height="100%"
          minHeight="0"
          overflow="hidden"
        >
          <Box
            style={{
              flexShrink: 0,
              padding: '12px 12px 8px',
              borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            }}
          >
            <Typography variant="subtitle2">Report Selections</Typography>
            <Typography variant="caption" color="text.secondary">
              {sections.length} sections in this report
            </Typography>
          </Box>
          <FlexContainer
            direction="column"
            flexGrow={1}
            minHeight="0"
            height="auto"
            width="100%"
            overflowY="auto"
            overflowX="hidden"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sections.map(section => section.id)}
                strategy={verticalListSortingStrategy}
              >
                <FlexContainer
                  direction="column"
                  gap={0}
                  width="100%"
                  height="auto"
                  overflowAllVisible={true}
                >
                  {sections.map((section, index) => (
                    <SortableSectionRow
                      key={section.id}
                      section={section}
                      index={index}
                      isSelected={selectedSectionId === section.id}
                      isLast={index === sections.length - 1}
                      savingSection={savingSection}
                      onSelect={onSelect}
                      onIncludedChange={onIncludedChange}
                      onCalloutEnabledChange={onCalloutEnabledChange}
                    />
                  ))}
                  <Box style={{ height: 8, flexShrink: 0 }} aria-hidden />
                </FlexContainer>
              </SortableContext>
            </DndContext>
          </FlexContainer>
        </FlexContainer>
      </ReportsSurface>
    </FlexContainer>
  );
};

export default ReportBuilderSectionList;
